// ============================================================================
// ROLE MASTER SCREEN — Phase 4 Administration
// ============================================================================
// Lists admin.tbl_role with grid + filters + add/edit/delete. Backed by
// roleService and the standard server-side pagination pattern.
//
// System roles (is_system_role=true) cannot be deleted — server returns 409
// and we surface the message in a toast.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ColumnSelector, type ColumnDefinition } from 'components/common';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  MinimizedModalStrip,
} from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useRoles } from 'hooks/api/useRoles';
import { roleService } from 'api/services/roleService';
import type { Role, RoleSearchRequest } from 'api/types/role.types';
import RoleFormModal from 'components/modals/modules/role-module/RoleFormModal';

interface RoleMasterScreenProps {
  tabId?: string;
  moduleName?: string;
}

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'Active only' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'DELETED', label: 'Deleted' },
  { value: 'ALL', label: 'All' },
];

const SYSTEM_ROLE_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'true', label: 'System only' },
  { value: 'false', label: 'Custom only' },
];

const RoleMasterScreen: React.FC<RoleMasterScreenProps> = ({ tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  const [queryParams, setQueryParams] = useState<RoleSearchRequest>({
    page: 1,
    pageSize: 25,
  });
  const { data, loading, pagination, refetch } = useRoles(queryParams);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  const [roleCodeFilter, setRoleCodeFilter] = useState('');
  const [roleNameFilter, setRoleNameFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isSystemFilter, setIsSystemFilter] = useState('');

  useEffect(() => {
    const t = setTimeout(() => {
      setQueryParams((prev) => ({
        ...prev,
        searchTerm: searchQuery.trim() || undefined,
        page: 1,
      }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy' | 'view'>('create');
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);

  // Minimize support: each Add gets a unique instance id; formModalMinimizeRef
  // lets us auto-minimize the open modal before opening another Add.
  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const handleRestoreModal = useCallback(
    (restoredMode: 'create' | 'edit' | 'copy' | 'view', instanceId?: string, recordCode?: string) => {
      if (instanceId) setCreateInstanceId(instanceId);
      if (recordCode) {
        const rec = data.find((r) => r.roleCode === recordCode);
        if (rec) setSelectedRole(rec);
      }
      setFormMode(restoredMode);
      setFormModalOpen(true);
    },
    [data],
  );

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);

  const handleAddNew = useCallback(() => {
    if (formModalOpen) {
      formModalMinimizeRef.current?.();
    }
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setSelectedRole(null);
    setFormMode('create');
    setFormModalOpen(true);
  }, [formModalOpen]);

  const handleEdit = useCallback(
    (role: Role) => {
      const modalId = tabId ? `${tabId}-form-modal-edit-${role.roleCode}` : null;
      if (modalId && isMinimized(modalId)) {
        const modal = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        modal?.onRestore?.();
        return;
      }
      setSelectedRole(role);
      setFormMode('edit');
      setFormModalOpen(true);
    },
    [tabId, isMinimized, dismissMinimized, getMinimizedModal],
  );

  const handleView = useCallback(
    (role: Role) => {
      const modalId = tabId ? `${tabId}-view-modal-${role.roleCode}` : null;
      if (modalId && isMinimized(modalId)) {
        const modal = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        modal?.onRestore?.();
        return;
      }
      setSelectedRole(role);
      setFormMode('view');
      setFormModalOpen(true);
    },
    [tabId, isMinimized, dismissMinimized, getMinimizedModal],
  );

  const handleCopy = useCallback(
    (role: Role) => {
      setSelectedRole(role);
      setFormMode('copy');
      setFormModalOpen(true);
    },
    [],
  );

  const handleDeleteClick = useCallback((role: Role) => {
    if (role.isSystemRole) {
      notificationService.toast.warning(
        `"${role.roleCode}" is a system role and cannot be deleted.`,
        5000,
      );
      return;
    }
    setRoleToDelete(role);
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!roleToDelete) return;
    setIsDeleting(true);
    try {
      await roleService.delete(roleToDelete.id);
      setIsDeleting(false);
      setDeleteModalOpen(false);
      notificationService.toast.success(`Role "${roleToDelete.roleCode}" deleted successfully.`);
      setRoleToDelete(null);
      refetch();
    } catch (err: any) {
      setIsDeleting(false);
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.message ||
        (status === 409 ? 'System roles cannot be deleted.' : 'An error occurred while deleting.');
      notificationService.toast.error(msg, 6000);
    }
  }, [roleToDelete, refetch]);

  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams((prev) => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((pageSize: number) => {
    setQueryParams((prev) => ({ ...prev, pageSize, page: 1 }));
  }, []);

  const handleApplyFilters = useCallback(() => {
    setQueryParams((prev) => ({
      ...prev,
      roleCode: roleCodeFilter.trim() || undefined,
      roleName: roleNameFilter.trim() || undefined,
      statusCode: statusFilter || undefined,
      isSystemRole: isSystemFilter === '' ? undefined : isSystemFilter === 'true',
      page: 1,
    }));
    setFilterOpen(false);
  }, [roleCodeFilter, roleNameFilter, statusFilter, isSystemFilter]);

  const handleClearFilters = useCallback(() => {
    setRoleCodeFilter('');
    setRoleNameFilter('');
    setStatusFilter('');
    setIsSystemFilter('');
    setQueryParams({ page: 1, pageSize: queryParams.pageSize ?? 25 });
  }, [queryParams.pageSize]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (queryParams.roleCode) count++;
    if (queryParams.roleName) count++;
    if (queryParams.statusCode) count++;
    if (queryParams.isSystemRole !== undefined) count++;
    return count;
  }, [queryParams.roleCode, queryParams.roleName, queryParams.statusCode, queryParams.isSystemRole]);

  const columns: SyutGridColumn<Role>[] = useMemo(
    () => [
      { id: 'roleCode', header: 'Role Code', accessorKey: 'roleCode', size: 220, enableSorting: true },
      { id: 'roleName', header: 'Role Name', accessorKey: 'roleName', size: 240, enableSorting: true, flex: true },
      {
        id: 'modules',
        header: 'Modules',
        size: 100,
        cell: ({ row }) => row.original.visibleModules?.length ?? 0,
      },
      {
        id: 'isSystemRole',
        header: 'Type',
        size: 100,
        cell: ({ row }) => {
          const sys = row.original.isSystemRole;
          return (
            <span
              style={{
                fontSize: '0.7rem',
                fontWeight: 600,
                color: sys ? '#0369a1' : '#16a34a',
                background: sys ? '#e0f2fe' : '#dcfce7',
                border: `1px solid ${sys ? '#0284c7' : '#16a34a'}`,
                padding: '2px 8px',
                borderRadius: '999px',
              }}
            >
              {sys ? 'SYSTEM' : 'CUSTOM'}
            </span>
          );
        },
      },
      {
        id: 'statusCode',
        header: 'Status',
        accessorKey: 'statusCode',
        size: 110,
        cell: ({ row }) => {
          const r = row.original;
          const code = r.deletedAt ? 'DELETED' : (r.statusCode || 'ACTIVE');
          let color = '#6b7280';
          if (code === 'ACTIVE') color = '#16a34a';
          else if (code === 'DELETED') color = '#dc2626';
          return (
            <span
              style={{
                fontSize: '0.75rem',
                fontWeight: 600,
                color,
                padding: '2px 8px',
                border: `1px solid ${color}`,
                borderRadius: '999px',
              }}
            >
              {code}
            </span>
          );
        },
      },
      {
        id: 'updatedAt',
        header: 'Last Updated',
        accessorKey: 'updatedAt',
        size: 160,
        cell: ({ row }) => {
          const v = row.original.updatedAt;
          if (!v) return '-';
          try { return new Date(v).toLocaleString(); } catch { return v; }
        },
      },
    ],
    [],
  );

  // Column visibility (driven by ColumnSelector toolbar control)
  const columnDefinitions: ColumnDefinition[] = useMemo(
    () =>
      columns.map((col) => ({
        id: col.id as string,
        label: typeof col.header === 'string' ? (col.header as string) : (col.id as string),
        defaultVisible: true,
      })),
    [columns],
  );
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
    columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id),
  );
  const handleApplyColumns = useCallback((cols: string[]) => setVisibleColumns(cols), []);
  const handleResetColumns = useCallback(() => {
    setVisibleColumns(columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id));
  }, [columnDefinitions]);

  const getRowActions = useCallback(
    (role: Role): ActionMenuItem[] => {
      const items: ActionMenuItem[] = [
        { id: 'view', label: 'View Details', icon: <IconSystem.eye size={16} />, onClick: () => handleView(role) },
        { id: 'edit', label: 'Edit', icon: <IconSystem.edit size={16} />, onClick: () => handleEdit(role) },
        { id: 'copy', label: 'Copy', icon: <IconSystem.copy size={16} />, onClick: () => handleCopy(role) },
      ];
      if (!role.isSystemRole) {
        items.push({
          id: 'delete',
          label: 'Delete',
          icon: <IconSystem.trash size={16} />,
          onClick: () => handleDeleteClick(role),
          variant: 'danger',
        });
      }
      return items;
    },
    [handleEdit, handleView, handleCopy, handleDeleteClick],
  );

  const actionButtons: ActionButton[] = useMemo(
    () => [
      {
        id: 'add',
        icon: <IconSystem.add size={18} />,
        label: 'Add',
        tooltip: 'Add Role',
        onClick: handleAddNew,
        variant: 'icon',
      },
      {
        id: 'refresh',
        icon: <IconSystem.refresh size={18} />,
        label: 'Refresh',
        tooltip: 'Refresh',
        onClick: () => refetch(),
        variant: 'icon',
      },
    ],
    [handleAddNew, refetch],
  );

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Role Master"
        description="Define roles with module visibility and per-module action permissions."
        icon={<IconSystem.users size={18} />}
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search roles...',
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen || activeFilterCount > 0,
          activeFilterCount,
        }}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
                visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <SyutGrid
              data={data}
              columns={columns}
              enableSorting
              enablePagination
              manualPagination
              totalCount={pagination.totalItems}
              currentPage={(queryParams.page ?? 1) - 1}
              pageSize={queryParams.pageSize ?? 25}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              visibleColumns={visibleColumns}
              rowActions={getRowActions}
              isLoading={loading}
              emptyMessage="No roles found"
            />
          </div>
        </div>

        {/* Filter panel — conditional render (matches Zone pattern). Sibling of the grid flex wrapper. */}
        {filterOpen && (
        <div className="search-panel">
          <div className="search-panel-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <IconSystem.filter size={14} />
              <h3 className="search-panel-title">Filters</h3>
            </div>
            <button
              onClick={() => { handleClearFilters(); setFilterOpen(false); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', display: 'flex', alignItems: 'center', color: 'var(--text-muted)', borderRadius: '0.25rem' }}
              title="Close filters"
            >
              <IconSystem.x size={16} />
            </button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Role Code</label>
              <input
                type="text"
                className="form-input"
                value={roleCodeFilter}
                onChange={(e) => setRoleCodeFilter(e.target.value)}
                placeholder="e.g. recsuit_admin"
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Role Name</label>
              <input
                type="text"
                className="form-input"
                value={roleNameFilter}
                onChange={(e) => setRoleNameFilter(e.target.value)}
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Type"
                options={SYSTEM_ROLE_FILTER_OPTIONS}
                value={isSystemFilter}
                onChange={(v) => setIsSystemFilter((v as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Status"
                options={STATUS_FILTER_OPTIONS}
                value={statusFilter}
                onChange={(v) => setStatusFilter((v as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={handleClearFilters}>Clear</button>
            <button className="btn btn-primary" onClick={handleApplyFilters}>Apply</button>
          </div>
        </div>
        )}
      </div>

      <ConfirmDialog
        open={deleteModalOpen}
        title="Delete Role"
        message={
          roleToDelete
            ? `Delete role "${roleToDelete.roleCode}"? This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting..."
        onConfirm={handleConfirmDelete}
        onCancel={() => { setDeleteModalOpen(false); setRoleToDelete(null); }}
      />

      <RoleFormModal
        isOpen={formModalOpen}
        mode={formMode}
        record={selectedRole}
        onSaved={() => refetch()}
        onClose={() => setFormModalOpen(false)}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId}
        minimizeRef={formModalMinimizeRef}
      />
    </div>
  );
};

export default RoleMasterScreen;
